import { Injectable, NgZone } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';

import { environment } from 'src/environments/environment';
import { PortalAuthService } from '../auth/portal-auth.service';
import { AlertType } from '../components/global-alert/alert-type.enum';
import { AlertService } from './alert.service';
import {
  clearAuth0TokenCache,
  clearIdleState,
  consumeSessionExpired,
  markSessionExpired,
  readLastActivity,
  writeLastActivity,
} from '../auth/idle-state';

/** Warning shown this long before the logout (shorter timeouts, e.g. in the mock mode, use half the timeout). */
const WARNING_MS = 60_000;
/** Activity is recorded at most this often, so scroll and pointer events stay cheap. */
const ACTIVITY_THROTTLE_MS = 1_000;
const ACTIVITY_EVENTS = ['click', 'keydown', 'pointerdown', 'scroll'] as const;

/**
 * Ends the session after a period without user activity (default 30 minutes, `environment.idleTimeoutMinutes`).
 *
 * The tokens live in sessionStorage (SessionStorageCache), so an unattended, unlocked screen would otherwise stay
 * signed in as long as the tab exists. One minute before the end an alert offers to stay signed in; without a
 * reaction the Auth0 token cache is wiped and PortalAuthService.logout() runs. The last activity is kept in
 * sessionStorage, so reloading the tab or waking a sleeping laptop cannot extend the session.
 *
 * The logout is a full redirect and does not go through the unsaved-changes guard on purpose: the user has not
 * touched the page for the whole period, and the warning gave a last chance to keep it.
 *
 * Listeners and timers run outside the Angular zone, so scrolling and clicking cause no change detection.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private readonly timeoutMs = environment.idleTimeoutMinutes * 60_000;
  private readonly warningMs = Math.min(WARNING_MS, this.timeoutMs / 2);

  private running = false;
  private lastActivity = 0;
  private warningTimer?: ReturnType<typeof setTimeout>;
  private countdownTimer?: ReturnType<typeof setInterval>;
  private warningAlert?: HTMLIonAlertElement;
  private warningOpen = false;

  private readonly onActivity = (): void => {
    if (this.warningOpen) return; // only the alert's own button may extend a warned session
    const now = Date.now();
    if (now - this.lastActivity < ACTIVITY_THROTTLE_MS) return;
    this.recordActivity(now);
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    // Timers are throttled in background tabs: judge by the clock, not by whether a timer fired.
    const stored = readLastActivity() ?? this.lastActivity;
    const idleFor = Date.now() - stored;
    if (idleFor >= this.timeoutMs) {
      this.expire(true);
    } else if (idleFor >= this.timeoutMs - this.warningMs) {
      this.lastActivity = stored;
      void this.showWarning();
    } else {
      this.onActivity();
    }
  };

  constructor(
    private readonly zone: NgZone,
    private readonly alertCtrl: AlertController,
    private readonly translate: TranslateService,
    private readonly auth: PortalAuthService,
    private readonly alerts: AlertService,
  ) {}

  /** Starts watching. Call once the user is authenticated; calling it again is a no-op. */
  start(): void {
    if (this.running) return;
    this.running = true;

    const stored = readLastActivity();
    if (stored !== null && Date.now() - stored >= this.timeoutMs) {
      this.expire(true);
      return;
    }

    this.zone.runOutsideAngular(() => {
      for (const name of ACTIVITY_EVENTS) {
        document.addEventListener(name, this.onActivity, { capture: true, passive: true });
      }
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    });
    this.recordActivity(Date.now());
  }

  /** Stops watching and forgets the state. Call when the user signs out. */
  stop(): void {
    if (!this.running) return;
    this.running = false;
    for (const name of ACTIVITY_EVENTS) {
      document.removeEventListener(name, this.onActivity, { capture: true });
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.clearTimers();
    void this.warningAlert?.dismiss();
    this.warningAlert = undefined;
    this.warningOpen = false;
    clearIdleState();
  }

  /** After an idle logout the next page load tells the user why they were signed out. Safe to call always. */
  showExpiredNoticeIfAny(): void {
    if (!consumeSessionExpired()) return;
    // At bootstrap the language file may still be loading: instant() would return the raw key. stream() emits the
    // key first in that case and the text once the language is there, so wait for the real text.
    const key = 'session.expired.toast';
    this.translate.stream(key).pipe(filter(text => text !== key), take(1))
      .subscribe(text => this.alerts.show(AlertType.Info, text));
  }

  private recordActivity(now: number): void {
    this.lastActivity = now;
    writeLastActivity(now);
    this.scheduleWarning();
  }

  private scheduleWarning(): void {
    clearTimeout(this.warningTimer);
    const remaining = this.lastActivity + this.timeoutMs - this.warningMs - Date.now();
    this.zone.runOutsideAngular(() => {
      this.warningTimer = setTimeout(() => void this.showWarning(), Math.max(remaining, 0));
    });
  }

  private clearTimers(): void {
    clearTimeout(this.warningTimer);
    clearInterval(this.countdownTimer);
    this.warningTimer = undefined;
    this.countdownTimer = undefined;
  }

  private secondsLeft(): number {
    return Math.max(Math.ceil((this.lastActivity + this.timeoutMs - Date.now()) / 1000), 0);
  }

  private async showWarning(): Promise<void> {
    if (this.warningOpen || !this.running) return;
    this.warningOpen = true;
    clearTimeout(this.warningTimer);

    this.countdownTimer = this.zone.runOutsideAngular(() => setInterval(() => {
      if (this.secondsLeft() <= 0) this.expire(true);
      else if (this.warningAlert) this.warningAlert.message = this.message();
    }, 1000));

    const alert = await this.alertCtrl.create({
      header: this.translate.instant('session.idle.title'),
      message: this.message(),
      backdropDismiss: false,
      keyboardClose: false,
      buttons: [
        { text: this.translate.instant('session.idle.logout'), role: 'destructive', handler: () => { this.expire(false); } },
        { text: this.translate.instant('session.idle.stay'), role: 'confirm', handler: () => { this.stay(); } },
      ],
    });
    this.warningAlert = alert;
    // The countdown may have ended while the alert was being created.
    if (!this.warningOpen) return;
    await alert.present();
  }

  private message(): string {
    return this.translate.instant('session.idle.message', { seconds: this.secondsLeft() });
  }

  private stay(): void {
    this.warningOpen = false;
    this.warningAlert = undefined;
    clearInterval(this.countdownTimer);
    this.countdownTimer = undefined;
    this.recordActivity(Date.now());
  }

  /** `notify`: the user did not react, so the next page load explains the logout (not for "Abmelden" in the alert). */
  private expire(notify: boolean): void {
    this.stop();
    if (notify) markSessionExpired();
    clearAuth0TokenCache();
    this.auth.logout();
  }
}
